import { FunctionComponent, useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";

import { GQLEnumPostStatusEnum } from "@framework/__generated__/graphql/enums";
import { ContentResourceAnalyticsContainer_contentResource$key } from "@framework/__generated__/relay/ContentResourceAnalyticsContainer_contentResource.graphql";
import { pageLoadUUID } from "@framework/ads/ads.utils";
import {
  googleAnalyticsAccountIds,
  googleAnalyticsSponsoredAccountIds,
} from "@framework/analytics/analytics.config";
import {
  getCommunityCode,
  getMembershipPromotions,
} from "@framework/analytics/analytics.utils";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";
import { getPageType } from "@voxmedia/duet-utils/analytics";
import { getCookie } from "@voxmedia/duet-utils/cookie";

interface Props {
  contentResource: ContentResourceAnalyticsContainer_contentResource$key;
  shouldEmitEvent?: boolean;
  /** Indicate that this is a boosted post, passing in the originating post's path */
  boostedPostPrimaryUrl?: string;
}

const PRODUCT_BLOCK_TYPES = [
  "ProductBlockType",
  "LegacyProductBlockType",
  "ProductsTableBlockType",
  "LegacyProductsTableBlockType",
];

const ContentResourceAnalyticsContainer: FunctionComponent<Props> = ({
  contentResource,
  shouldEmitEvent = true,
  boostedPostPrimaryUrl,
}) => {
  const node = useFragment(
    graphql`
      fragment ContentResourceAnalyticsContainer_contentResource on ContentResourceInterface {
        __typename

        id
        wpId
        status
        resourceType
        title
        permalink
        originalPublishedAt
        publishedAt
        hasAffiliateLinks
        primaryCampaign {
          advertiserName
          title
        }
        seo {
          headline
        }
        social {
          headline
        }
        promo {
          headline
        }
        wordCount

        ... on FeedContentResourceInterface {
          # @excludeCacheKey: assignment/unassignment of the package shadow taxonomy will result in
          # invalidation of the resource cache key, so including the key is not needed
          package @excludeCacheKey {
            id
          }
          streams(first: 100) {
            # @excludeCacheKey: adding or removing a resource from a stream will result in
            # invalidation of the resource cache key.
            nodes @excludeCacheKey {
              id
            }
          }
          # @excludeCacheKey: assignment/unassignment of authors will result in invalidation of the
          # resource cache key, so including the key is not needed
          authors @excludeCacheKey {
            name
          }
          # @excludeCacheKey: assignment/unassignment of superCats will result in invalidation
          # of the resource cache key, so including the key is not needed
          internalSuperCats @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of superCats will result in invalidation
          # of the resource cache key, so including the key is not needed
          superCats @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          internalCategories @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          categories @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of primary category will result in invalidation
          # of the resource cache key, so including the key is not needed
          primaryCategory @excludeCacheKey {
            slug
          }
          liveStatus {
            startAt
          }

          blocks {
            __typename
            ... on ProductBlockType {
              product {
                __typename
              }
            }

            ... on LegacyProductBlockType {
              legacyProduct {
                __typename
              }
            }

            ... on ProductsTableBlockType {
              products {
                __typename
              }
            }

            ... on LegacyProductsTableBlockType {
              legacyProducts {
                __typename
              }
            }

            ... on HighlightBlockType {
              __typename
            }
          }

          ... on PostResourceType {
            template
          }
        }
        ... on StreamResourceType {
          streamPublishedAt
        }
      }
    `,
    contentResource,
  );

  const analytics = useAnalytics();
  const auth = useAuth();
  const emitted = useRef(false);

  const {
    communityConfig: {
      communityName,
      isPrimaryCommunity,
      communityCanonicalDomain,
    },
    networkConfig: { networkName },
  } = useConfigContext();

  useEffect(() => {
    if (
      !shouldEmitEvent ||
      !analytics ||
      auth.userLoading ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- GraphQL string status vs enum
      node.status !== GQLEnumPostStatusEnum.publish
    ) {
      return;
    }
    // TODO (cvle): This is a temporary fix. Need to investigate why this effect runs twice.
    if (emitted.current) {
      return;
    }

    const {
      id,
      wpId,
      resourceType,
      promo,
      social,
      seo,
      streams,
      authors,
      title,
      liveStatus,
      hasAffiliateLinks,
      primaryCampaign,
      wordCount,
      package: pkg,
      template,
      blocks,
    } = node;

    const primaryCategory = node.primaryCategory?.slug;
    const analyticsCategories = (node.categories || [])
      .concat(node.superCats || [])
      .map((category) => category.slug);

    if (node.resourceType === "package") {
      analyticsCategories.push(`package-${node.id}`);
    }

    if (pkg) {
      analyticsCategories.push(`package-${pkg.id}`);
    }

    const internalCategories = (node.internalCategories || [])
      .concat(node.internalSuperCats || [])
      .map(({ slug }) => slug)
      .join(",");

    const storyStreamIds =
      resourceType !== "stream" && streams?.nodes.length
        ? streams.nodes.map(({ id }) => id)
        : [];

    const communityCode = getCommunityCode(
      communityName,
      communityCanonicalDomain,
      isPrimaryCommunity,
    );

    // `All Categories` includes the main categories with some additional metadata
    const allAnalyticsCategories = [
      // Always include community and network name (which may be the same)
      communityCode,
      networkName.toLowerCase().replace(/ /g, ""),
      // Include the main categories
      analyticsCategories.join(","),
      // Also include internal categories
      internalCategories,
      // Reference any streams the post belongs to
      ...storyStreamIds,
    ];

    const originalPublishedAt = node.originalPublishedAt
      ? new Date(node.originalPublishedAt).toISOString()
      : null;

    const publishedAtTS = node.streamPublishedAt || node.publishedAt;
    const publishedAt = publishedAtTS
      ? new Date(publishedAtTS).toISOString()
      : null;

    const userId = auth.user?.uid || auth.viewer?.id || null;
    const isEntitled = auth.user?.isEntitled || false;

    let event: Record<string, any> = {
      ["All Categories"]: [
        ...new Set(allAnalyticsCategories.filter(Boolean)),
      ].join(","),
      Author: authors?.map((author) => author.name).join(",") || "",
      Community: communityCode,
      event: "dataLayer-initalized",
      Headline: title,
      ["Inclusion of Commerce Disclosure"]: hasAffiliateLinks,
      "Live Blog": liveStatus?.startAt ? true : false,
      ["Modified Date"]: publishedAt,
      Network: networkName.toLowerCase().replace(/ /g, ""),
      ["Original Publish Time"]: originalPublishedAt,
      ["Page ID"]: id,
      ["Page Type Category"]: "storytelling",
      // Page Type Category can be one of: Storytelling, Aggregation, Supporting, System.
      ["Page Type"]: getPageType(resourceType, !!template),
      ["Post ID"]: wpId,
      ["Promo Headline"]: promo.headline,
      ["SEO Headline"]: seo.headline,
      ["Social Headline"]: social.headline,
      ["Story Streams"]: storyStreamIds.length
        ? storyStreamIds.join(",")
        : null,
      ["Unique Pageload ID"]: pageLoadUUID(),
      "User ID": userId ? String(userId) : "Logged Out",
      ["Word Count"]: wordCount,
      isEntitled,
      ...googleAnalyticsAccountIds,
    };

    // the `Categories` field includes the main categories
    const mainCategories = [...analyticsCategories];

    if (mainCategories.length > 0) {
      event.Categories = mainCategories.join(",");
    }

    if (internalCategories.length > 0) {
      event["Internal Categories"] = internalCategories;
    }

    if (primaryCategory) {
      event["Primary Category"] = primaryCategory;
    }

    const primaryCampaignAdvertiser =
      primaryCampaign?.advertiserName || primaryCampaign?.title;

    if (primaryCampaignAdvertiser) {
      event["Sponsored Post Advertiser"] = primaryCampaignAdvertiser;
      event = {
        ...event,
        ...googleAnalyticsSponsoredAccountIds,
      };
    }

    // Add membership promotions to the event
    const membershipPromotions = getMembershipPromotions(auth.user?.promotions);
    if (membershipPromotions) {
      event["Membership Promotions"] = membershipPromotions;
    }

    // Get a user's Sailthru ID `sailthru_hid` if stored in cookies.
    const sailthruUserID = getCookie("sailthru_hid");
    if (sailthruUserID) {
      event["Sailthru User ID"] = sailthruUserID;
    }

    // Check if user is a Scroll subscriber in cookies.
    event["Scroll Subscription"] = getCookie("scroll0") ? true : false;

    // Filter out blocks that are product related
    const productCards = blocks?.filter((block) =>
      PRODUCT_BLOCK_TYPES.includes(block.__typename),
    );

    // Get total number of products mentioned in an article,
    // [legacy] product block has 1 max, products table can have many
    if (productCards && productCards.length) {
      let numProductCards = 0;
      productCards.forEach((block) => {
        if (block.__typename === "ProductBlockType") {
          numProductCards += 1;
        }
        if (block.__typename === "LegacyProductBlockType") {
          numProductCards += 1;
        }
        if (block.__typename === "ProductsTableBlockType") {
          numProductCards += block.products.length || 0;
        }
        if (block.__typename === "LegacyProductsTableBlockType") {
          numProductCards += block.legacyProducts.length || 0;
        }
      });

      event["Number of Product Cards"] = numProductCards;
    }

    // Add additional metadata for boosted posts and format like other
    // (non-dataLayer-initialized) events
    let structuredEvent = event;
    if (boostedPostPrimaryUrl) {
      event["Primary URL"] = boostedPostPrimaryUrl;
      event["Native Boost"] = true;
      delete event["event"];
      structuredEvent = {
        event: "native_boost_page_view_wordpress",
        native_boost_page_view_wordpress: event,
      };
    }
    // Add additional metadata for Highlight blocks
    if (blocks?.some((block) => block.__typename === "HighlightBlockType")) {
      event["Highlight Bar"] = true;
      event["Highlight Bar In First Three Blocks"] = blocks
        .slice(0, 3)
        .some((block) => block.__typename === "HighlightBlockType");
    } else {
      event["Highlight Bar"] = false;
      event["Highlight Bar In First Three Blocks"] = false;
    }

    emitted.current = true;
    analytics.emit(structuredEvent);
  }, [
    analytics,
    auth,
    boostedPostPrimaryUrl,
    communityName,
    communityCanonicalDomain,
    isPrimaryCommunity,
    networkName,
    node,
    shouldEmitEvent,
  ]);

  return null;
};

export default withGracefulErrorBoundary(ContentResourceAnalyticsContainer, {
  context: "ContentResourceAnalyticsContainerErrorBoundary",
});
